import React, { FunctionComponent } from "react";
import styled from "styled-components";
import Image from "next/image";

import { colours } from "utils";
import { CXL, CL, CM } from "@/components/Typography";
import Device from "@/utils/device";
import StepForwardTitle from "../step-forward/StepForwardTitle";
import { getStepForwardArticles } from "@/components/hooks/query/useArticleQuery";
import { useRouter } from "next/router";

type StyleProps = {
  background?: string;
  textColour?: string;
};

const ArticlesContent: FunctionComponent = () => {
  const router = useRouter();
  const articles = getStepForwardArticles();

  const linkHandler = async (url: string) => {
    await router.push("/edit/articles/" + url + "#from-top");
  };

  if (articles.isLoading) return null;

  return (
    <S.Section data-cy="SF-content-themes">
      <S.CopyContainer>
        <StepForwardTitle>
          <S.FontWeightWrap>
            The Latest
            <br />
          </S.FontWeightWrap>
          Step Forward Articles
        </StepForwardTitle>
        <CXL>
          <b>
            Delve into innovative content, articles and perspectives from the
            Step Forward community.
          </b>
        </CXL>
      </S.CopyContainer>
      <S.ArticlesContainer>
        {articles?.data?.map((article) => (
          <S.ArticleCard
            key={article.title}
            onClick={() => linkHandler(article.url)}
          >
            <S.ArticleImage>
              <Image
                src={article.heroImage.image.url}
                layout="fill"
                objectFit="cover"
              />
            </S.ArticleImage>
            <S.ArticleCopyContainer>
              <CM weight={700} color="grey">
                {article.topic}
              </CM>
              <CM>
                <b>{article.title}</b>
              </CM>
            </S.ArticleCopyContainer>
          </S.ArticleCard>
        ))}
      </S.ArticlesContainer>
    </S.Section>
  );
};

const S = () => {};

S.Section = styled.section`
  position: relative;
  width: 100%;
  background: ${colours.stepForwardLilac};
  padding: 10%;

  @supports not (-webkit-touch-callout: none) {
    background-attachment: fixed;
  }

  @media ${Device.tablet} {
    padding: 5% 10%;
  }
`;

S.CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > ${CXL} {
    line-height: 2rem;
  }

  @media ${Device.mobile} {
    gap: 30px;

    > ${CXL} {
      line-height: 2.5rem;
    }
  }

  @media ${Device.desktop} {
    gap: 30px;

    > ${CXL} {
      line-height: 2.8rem;
    }
  }
`;

S.FontWeightWrap = styled.span`
  font-weight: 100;
`;

S.ArticlesContainer = styled.div`
  margin: 40px 0 0 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;

  > * > ${CL} {
    line-height: 1.6rem;
  }

  @media ${Device.mobile} {
    > * > ${CL} {
      line-height: 2.5rem;
    }
  }

  @media ${Device.tablet} {
    margin: 60px 0 0 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-gap: 60px;
  }

  @media ${Device.desktop} {
    > * > ${CL} {
      line-height: 2.8rem;
    }
  }
`;

S.ArticleCard = styled.li<StyleProps>`
  box-shadow: 0px 4px 4px 0px #c3afa1;
  list-style-type: none;
  flex-shrink: 0;
  height: fit-content;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
    filter: brightness(1.1);
  }

  @media (min-width: ${Device.mobile}) {
    margin: 0 0 0 30px;
    width: 100%;
  }
`;

S.ArticleImage = styled.div`
  position: relative;
  aspect-ratio: 1;
  width: 100%;

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: "";
    }
    &::after {
      display: block;
      content: "";
      clear: both;
    }
  }
`;

S.ArticleCopyContainer = styled.div`
  padding: 15px 20px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export default ArticlesContent;
